<!-- src/components/Header.vue -->
<!-- Author Igor Veynik | Site: NexusLab.ru | Telegram: @thenexus | created 30.10.2026 | updated 30.10.2026 -->
<template>
  <header class="topbar">
    <button type="button" class="btn btn-ghost btn-icon burger" title="Меню" @click="$emit('open-nav')">
      <font-awesome-icon icon="bars" />
    </button>

    <nav class="crumbs" aria-label="Навигация">
      <template v-for="(crumb, idx) in crumbs" :key="crumb.name">
        <span v-if="idx > 0" class="crumb-sep">/</span>
        <router-link
            v-if="idx < crumbs.length - 1"
            :to="{ name: crumb.name }"
            class="crumb"
        >{{ crumb.title }}</router-link>
        <span v-else class="crumb crumb-current">{{ crumb.title }}</span>
      </template>
    </nav>

    <div v-if="isAuthenticated" ref="userBox" class="user" :class="{ open: showDropdown }">
      <button type="button" class="user-btn" @click="showDropdown = !showDropdown">
        <span class="avatar">{{ initials }}</span>
        <span class="user-name">{{ displayName }}</span>
        <font-awesome-icon icon="chevron-down" class="user-chevron" />
      </button>

      <transition name="drop">
        <div v-if="showDropdown" class="dropdown">
          <div class="dropdown-head">
            <div class="dropdown-name">{{ displayName }}</div>
            <div class="dropdown-login">{{ currentUser.login }}</div>
          </div>
          <router-link :to="{ name: 'settings' }" class="dropdown-item" @click="showDropdown = false">
            <font-awesome-icon icon="key" /> Сменить пароль
          </router-link>
          <button type="button" class="dropdown-item danger" @click="logout">
            <font-awesome-icon icon="right-from-bracket" /> Выйти
          </button>
        </div>
      </transition>
    </div>
  </header>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Header',
  emits: ['open-nav'],
  data() {
    return {
      showDropdown: false
    }
  },
  computed: {
    ...mapGetters(['isAuthenticated', 'currentUser']),
    displayName() {
      const u = this.currentUser || {}
      const full = [u.name, u.surname].filter(Boolean).join(' ')
      return full || u.login || ''
    },
    initials() {
      const u = this.currentUser || {}
      const parts = [u.name, u.surname].filter(Boolean)
      const src = parts.length ? parts.map(p => p[0]).join('') : (u.login || '?').slice(0, 2)
      return src.toUpperCase()
    },
    crumbs() {
      const list = this.$route.matched
          .filter(r => r.meta?.title)
          .map(r => ({ name: r.name, title: r.meta.title }))
      return list.length ? list : [{ name: 'main', title: 'Главная' }]
    }
  },
  mounted() {
    document.addEventListener('click', this.onOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutside)
  },
  methods: {
    onOutside(e) {
      if (this.showDropdown && this.$refs.userBox && !this.$refs.userBox.contains(e.target)) {
        this.showDropdown = false
      }
    },
    logout() {
      this.showDropdown = false
      this.$store.dispatch('logout')
      this.$router.push({ name: 'login' })
    }
  }
}
</script>

<style lang="scss" scoped>
.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 12px;
  height: var(--topbar-h);
  padding: 0 28px;
  background: rgba(244, 246, 250, .85);
  backdrop-filter: saturate(180%) blur(10px);
  border-bottom: 1px solid var(--border);
}

.burger { display: none; }

.crumbs {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
  font-size: 15px;
  white-space: nowrap;
  overflow: hidden;
}
.crumb { color: var(--muted); font-weight: 500; }
.crumb:hover { color: var(--text); }
.crumb-current { color: var(--text); font-weight: 650; overflow: hidden; text-overflow: ellipsis; }
.crumb-sep { color: var(--border-strong); }

.user { position: relative; }
.user-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 10px 0 4px;
  border: 1px solid transparent;
  border-radius: 999px;
  background: transparent;
  cursor: pointer;
  transition: background-color .15s var(--ease), border-color .15s var(--ease);
}
.user-btn:hover, .user.open .user-btn { background: var(--surface); border-color: var(--border); }
.avatar {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: #fff;
  font-size: 12px;
  font-weight: 650;
  letter-spacing: .02em;
}
.user-name { font-size: 14px; font-weight: 550; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.user-chevron { font-size: 11px; color: var(--muted); transition: transform .15s var(--ease); }
.user.open .user-chevron { transform: rotate(180deg); }

.dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  min-width: 240px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  padding: 6px;
}
.dropdown-head { padding: 10px 12px 12px; border-bottom: 1px solid var(--border); margin-bottom: 6px; }
.dropdown-name { font-weight: 600; }
.dropdown-login { color: var(--muted); font-size: 12px; }
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--text);
  font-size: 14px;
  cursor: pointer;
  text-align: left;
}
.dropdown-item:hover { background: var(--surface-3); color: var(--text); }
.dropdown-item.danger { color: var(--danger-text); }
.dropdown-item.danger:hover { background: var(--danger-soft); }

.drop-enter-active, .drop-leave-active { transition: opacity .12s var(--ease), transform .12s var(--ease); }
.drop-enter-from, .drop-leave-to { opacity: 0; transform: translateY(-4px); }

@media (max-width: 960px) {
  .topbar { padding: 0 12px; }
  .burger { display: inline-flex; }
  .user-name, .user-chevron { display: none; }
  .user-btn { padding: 0 4px; }
}
</style>
